import { z } from 'zod'
import { ajouterJours, ajouterJoursOuvres, type CalendrierOuvre } from './calendrier'
import type {
  MoyenPaiement,
  RoleTiers,
  SensPaiement,
  StatutEcheance,
  StatutPaiement,
  StatutTache,
  TypePersonne,
} from './domaine'
import { formaterPeriode } from './periode'

// Tâches : générateur (échéances, paiements, modèles -> tâches à créer ou à recaler)
// et affectation (exécutant, garant, « prendre »). Fonctions pures.

/**
 * Qui exécute une tâche générée : les responsables (à prendre), le cabinet de l'entité,
 * l'assistant, ou le gestionnaire du bail de la règle (concierge, agence).
 */
export const EXECUTANTS_MODELE = ['responsables', 'cabinet', 'assistant', 'gestionnaire'] as const
export type ExecutantModele = (typeof EXECUTANTS_MODELE)[number]

/**
 * Modèle de tâche d'une règle d'échéance. La date due suit la date limite de l'échéance
 * (fin de fenêtre, sinon date) décalée de n jours ouvrés. L'action « payer » porte le
 * paiement de l'échéance : la terminer propose de le marquer payé.
 */
export const modeleTache = z.object({
  action: z.string().regex(/^[a-z0-9_]+$/, 'Code en minuscules : preparer, declarer, payer…'),
  titre: z.string().trim().min(1),
  decalageJoursOuvres: z.number().int().min(-250).max(60),
  executant: z.enum(EXECUTANTS_MODELE).optional(),
})
export type ModeleTache = z.infer<typeof modeleTache>

/** Un modèle confié au gestionnaire suppose une règle rattachée à un bail. */
export function modelesSansBail(
  modeles: readonly Pick<ModeleTache, 'executant'>[],
  bailId: string | null | undefined,
): string | null {
  if (bailId || !modeles.some((m) => m.executant === 'gestionnaire')) return null
  return 'Exécutant « gestionnaire » : rattacher la règle à un bail (son gestionnaire exécute).'
}

/** Actions qui portent un paiement : terminer la tâche propose de le marquer payé. */
export const ACTIONS_PAIEMENT = ['payer', 'regulariser_paiement'] as const

export const HORIZON_TACHES_JOURS = 45

/**
 * Démarches envers l'État (impôts, organismes, comptes annuels) : leurs tâches sont signalées
 * en rouge dans les listes. Par le type de la règle de l'échéance, ou du guide associé.
 */
export const TYPES_DECLARATION_ETAT = [
  'tva_ca3',
  'pajemploi',
  'is_acompte',
  'is_solde',
  'liasse_2031',
  'declaration_revenus',
  'approbation_comptes',
  'depot_comptes',
  'taxe_fonciere',
  'cfe',
  'cfe_1447c',
  'das2',
] as const

/** Une tâche de déclaration à l'État : sa règle ou son guide est d'un type de la liste. */
export function estDeclarationEtat(typeRegle: string | null, typeGuide: string | null): boolean {
  const types: readonly string[] = TYPES_DECLARATION_ETAT
  return (
    (typeRegle !== null && types.includes(typeRegle)) ||
    (typeGuide !== null && types.includes(typeGuide))
  )
}

/** Moyens qui demandent un geste humain (N2) : une tâche « payer » les rappelle. */
const MOYENS_A_EMETTRE: readonly MoyenPaiement[] = ['virement_emis', 'cheque']

export type EcheancePourTaches = {
  id: string
  entiteId: string
  libelle: string
  periode: string
  date: string
  finFenetre: string | null
  statut: StatutEcheance
  cycle: number
  modeles: readonly ModeleTache[]
}

export type PaiementPourTaches = {
  id: string
  entiteId: string
  echeanceId: string | null
  libelle: string | null
  datePrevue: string | null
  statut: StatutPaiement
  sens: SensPaiement
  moyen: MoyenPaiement | null
  /**
   * Créé par une pièce entrée par une recherche des pièces dans les mails anciens (rattrapage) :
   * le paiement reste prévu, sans tâche « Payer » à lui ; une tâche récapitulative du
   * rattrapage les liste. Jamais un paiement saisi avant elle, qu'elle n'a fait que lier.
   */
  pieceDeRecherche?: boolean
}

/** Tâche générée déjà en base (origine « generee »). */
export type TacheExistante = {
  id: string
  echeanceId: string | null
  paiementId: string | null
  action: string | null
  cycle: number
  statut: StatutTache
  dateDue: string | null
  dateForcee: boolean
  decalageJoursOuvres?: number | null
}

export type TacheACreer = {
  titre: string
  entiteId: string
  echeanceId: string | null
  paiementId: string | null
  action: string
  cycle: number
  decalageJoursOuvres: number
  dateDue: string | null
  executant: ExecutantModele
}

export type PlanTaches = {
  creer: TacheACreer[]
  recaler: { id: string; dateDue: string }[]
}

const OUVERTES: readonly StatutTache[] = ['a_faire', 'en_cours', 'en_attente_tiers']

function dateDue(limite: string, decalage: number, calendrier: CalendrierOuvre): string {
  return decalage === 0 ? limite : ajouterJoursOuvres(limite, decalage, calendrier)
}

/**
 * Plan du générateur. Une tâche générée est unique par (objet, action, cycle) ; après une
 * réouverture (cycle suivant), ce qui a déjà été fait n'est pas refait : seules les tâches
 * propres au nouveau cycle (régulariser un paiement rejeté…) s'y ajoutent.
 * Une tâche n'est créée qu'à l'approche de sa date (horizon en jours) ; elle suit ensuite
 * son objet tant qu'elle est ouverte et que sa date n'est pas forcée.
 */
export function planifierTaches(entree: {
  echeances: readonly EcheancePourTaches[]
  paiements: readonly PaiementPourTaches[]
  existantes: readonly TacheExistante[]
  aujourdhui: string
  horizonJours?: number
  calendrier: CalendrierOuvre
}): PlanTaches {
  const limiteCreation = ajouterJours(
    entree.aujourdhui,
    entree.horizonJours ?? HORIZON_TACHES_JOURS,
  )
  const plan: PlanTaches = { creer: [], recaler: [] }

  const cherche = (objet: 'echeanceId' | 'paiementId', id: string, action: string) =>
    entree.existantes.filter((t) => t[objet] === id && t.action === action)

  const suivre = (existantes: readonly TacheExistante[], cycle: number, due: string | null) => {
    for (const t of existantes) {
      if (t.cycle !== cycle || t.dateForcee || !OUVERTES.includes(t.statut)) continue
      if (due !== null && t.dateDue !== due) plan.recaler.push({ id: t.id, dateDue: due })
    }
  }

  for (const e of entree.echeances) {
    if (e.statut !== 'a_venir' && e.statut !== 'en_preparation') continue
    const limite = e.finFenetre ?? e.date
    for (const m of e.modeles) {
      const due = dateDue(limite, m.decalageJoursOuvres, entree.calendrier)
      const deja = cherche('echeanceId', e.id, m.action)
      if (deja.length > 0) {
        suivre(deja, e.cycle, due)
        continue
      }
      if (due > limiteCreation) continue
      plan.creer.push({
        titre: `${m.titre} (${formaterPeriode(e.periode)})`,
        entiteId: e.entiteId,
        echeanceId: e.id,
        paiementId: null,
        action: m.action,
        cycle: e.cycle,
        decalageJoursOuvres: m.decalageJoursOuvres,
        dateDue: due,
        executant: m.executant ?? 'responsables',
      })
    }
  }

  // Paiements à émettre hors échéance (les tâches d'une échéance viennent de sa règle). Celui
  // d'une pièce entrée par une recherche n'en ouvre pas : le rattrapage les récapitule.
  for (const p of entree.paiements) {
    if (p.echeanceId !== null || p.sens !== 'payer') continue
    if (!p.moyen || !MOYENS_A_EMETTRE.includes(p.moyen)) continue
    const deja = cherche('paiementId', p.id, 'payer')
    if (deja.length > 0) {
      if (p.statut === 'prevu') suivre(deja, 0, p.datePrevue)
      continue
    }
    if (p.statut !== 'prevu' || p.pieceDeRecherche) continue
    if (p.datePrevue !== null && p.datePrevue > limiteCreation) continue
    plan.creer.push({
      titre: `Payer : ${p.libelle ?? 'paiement'}`,
      entiteId: p.entiteId,
      echeanceId: null,
      paiementId: p.id,
      action: 'payer',
      cycle: 0,
      decalageJoursOuvres: 0,
      dateDue: p.datePrevue,
      executant: 'responsables',
    })
  }
  return plan
}

// Affectation

export type RoleSurEntite = { personneId: string; entiteId: string; responsable: boolean }

type TacheAffectee = {
  entiteId: string | null
  garantId: string | null
  executantPersonneId: string | null
  executantTiersId: string | null
}

/**
 * Garants d'une tâche : le garant désigné, sinon les responsables de l'entité (à défaut,
 * tous ceux qui y ont un rôle) ; sans entité, les administrateurs.
 */
export function garantsDe(
  tache: Pick<TacheAffectee, 'entiteId' | 'garantId'>,
  roles: readonly RoleSurEntite[],
  administrateurs: readonly string[],
): string[] {
  if (tache.garantId) return [tache.garantId]
  if (tache.entiteId === null) return [...administrateurs]
  const surEntite = roles.filter((r) => r.entiteId === tache.entiteId)
  const responsables = surEntite.filter((r) => r.responsable)
  return [...new Set((responsables.length > 0 ? responsables : surEntite).map((r) => r.personneId))]
}

/** Personne concernée : son exécutant, ou, sans exécutant désigné, chacun de ses garants. */
export function concerne(personneId: string, tache: TacheAffectee, garants: readonly string[]) {
  if (tache.executantPersonneId !== null) return tache.executantPersonneId === personneId
  return garants.includes(personneId)
}

/** Sans exécutant : visible de tous ses garants, le premier qui la prend en devient l'exécutant. */
export function aPrendre(tache: TacheAffectee): boolean {
  return tache.executantPersonneId === null && tache.executantTiersId === null
}

function aUnRole(
  personneId: string,
  entiteId: string | null,
  roles: readonly RoleSurEntite[],
  administrateurs: readonly string[],
) {
  if (entiteId === null) return administrateurs.includes(personneId)
  return roles.some((r) => r.personneId === personneId && r.entiteId === entiteId)
}

/** On n'affecte qu'à quelqu'un qui a un rôle sur l'entité ; l'assistant peut aussi exécuter. */
export function peutEtreExecutant(
  personne: { id: string; type: TypePersonne },
  entiteId: string | null,
  roles: readonly RoleSurEntite[],
  administrateurs: readonly string[],
): boolean {
  if (personne.type === 'assistant') return true
  return aUnRole(personne.id, entiteId, roles, administrateurs)
}

/**
 * Proposés pour exécuter une tâche (menu « Assigner à », champ Exécutant) : les humains qui
 * peuvent l'exécuter et les tiers qui agissent eux-mêmes sur nos dossiers, cabinet et
 * gestionnaire d'un bail (conciergerie). L'assistant et les autres tiers (fournisseurs, clients,
 * locataires, administrations) ne sont plus proposés, mais une tâche générée ou déjà confiée
 * garde son exécutant (décision de Pierre-Marie du 02/11/2026).
 */
export const ROLES_TIERS_EXECUTANTS = [
  'cabinet',
  'gestionnaire',
] as const satisfies readonly RoleTiers[]

export function proposeCommeExecutant(
  personne: { id: string; type: TypePersonne },
  entiteId: string | null,
  roles: readonly RoleSurEntite[],
  administrateurs: readonly string[],
): boolean {
  return personne.type === 'humain' && peutEtreExecutant(personne, entiteId, roles, administrateurs)
}

/** Le garant est toujours un humain ayant un rôle sur l'entité. */
export function peutEtreGarant(
  personne: { id: string; type: TypePersonne },
  entiteId: string | null,
  roles: readonly RoleSurEntite[],
  administrateurs: readonly string[],
): boolean {
  return personne.type === 'humain' && aUnRole(personne.id, entiteId, roles, administrateurs)
}

/** Ma semaine : en retard, sans date, ou due dans les sept jours. */
export function dansMaSemaine(dateDue: string | null, aujourdhui: string): boolean {
  return dateDue === null || dateDue <= ajouterJours(aujourdhui, 7)
}

/**
 * Chez un tiers : en attente de sa réponse, ou confiée à un tiers et ouverte tant qu'il n'a
 * pas répondu. Elle se suit dans « en attente de tiers », pas dans « ma semaine ».
 */
export function chezUnTiers(t: {
  statut: StatutTache
  executantTiersId: string | null
  reponseRecueLe: unknown
}): boolean {
  if (t.statut === 'en_attente_tiers') return true
  return OUVERTES.includes(t.statut) && t.executantTiersId !== null && t.reponseRecueLe === null
}

/**
 * Demande repérée dans un mail : dans « ma semaine » (et le digest) le jour de son arrivée
 * et le lendemain, même si sa date limite est plus loin.
 */
export function demandeRecente(
  t: { origine: string; creeLeJour: string },
  aujourdhui: string,
): boolean {
  return t.origine === 'mail' && t.creeLeJour >= ajouterJours(aujourdhui, -1)
}

// Report (décision de Pierre-Marie du 02/11/2026) : une tâche mise de côté sort des listes
// (ma semaine, tableau, attente de tiers, digest, pastille) jusqu'à son jour de réveil, puis
// revient « nouvelle » pour son exécutante. Reportée au bilan, elle suit l'échéance du bilan de
// son entité : elle revient le jour où le générateur crée la première tâche de sa préparation.

/** Règles dont l'échéance est le bilan de l'exercice (comptes annuels et liasse). */
export const TYPES_BILAN = ['is_solde', 'liasse_2031'] as const

/** Endormie : reportée à un jour qui n'est pas encore arrivé. */
export function estEndormie(reporteeAu: string | null, aujourdhui: string): boolean {
  return reporteeAu !== null && reporteeAu > aujourdhui
}

export type EcheancePourReport = {
  id: string
  date: string
  finFenetre: string | null
  modeles: readonly Pick<ModeleTache, 'decalageJoursOuvres'>[]
}

/**
 * Réveil d'une tâche reportée à une échéance : la première tâche de sa préparation (la date
 * limite décalée du plus de jours ouvrés) donne la date due, et la tâche revient le jour où le
 * générateur la crée (horizon).
 */
export function reveilAvecEcheance(
  e: Omit<EcheancePourReport, 'id'>,
  calendrier: CalendrierOuvre,
): { reporteeAu: string; dateDue: string } {
  const decalage =
    e.modeles.length > 0 ? Math.min(...e.modeles.map((m) => m.decalageJoursOuvres)) : 0
  const due = dateDue(e.finFenetre ?? e.date, decalage, calendrier)
  return { reporteeAu: ajouterJours(due, -HORIZON_TACHES_JOURS), dateDue: due }
}

/**
 * Tâches endormies dont l'échéance du report a bougé : leur réveil suit. Leur date due aussi,
 * tant qu'elle est celle que le report a posée (réveil plus horizon) ; changée depuis, elle reste.
 */
export function recalerReports(entree: {
  reportees: readonly {
    id: string
    reporteeEcheanceId: string
    reporteeAu: string | null
    dateDue: string | null
  }[]
  echeances: readonly EcheancePourReport[]
  aujourdhui: string
  calendrier: CalendrierOuvre
}): { id: string; reporteeAu: string; dateDue: string | null }[] {
  const parId = new Map(entree.echeances.map((e) => [e.id, e]))
  const recaler: { id: string; reporteeAu: string; dateDue: string | null }[] = []
  for (const t of entree.reportees) {
    const e = parId.get(t.reporteeEcheanceId)
    if (!e || !estEndormie(t.reporteeAu, entree.aujourdhui)) continue
    const attendu = reveilAvecEcheance(e, entree.calendrier)
    const posee = ajouterJours(t.reporteeAu!, HORIZON_TACHES_JOURS)
    const due = t.dateDue === posee ? attendu.dateDue : t.dateDue
    if (attendu.reporteeAu !== t.reporteeAu || due !== t.dateDue)
      recaler.push({ id: t.id, reporteeAu: attendu.reporteeAu, dateDue: due })
  }
  return recaler
}

// Schémas d'entrée, partagés par l'API et le MCP.

const texte = z.string().trim().min(1)

export const EVENEMENTS_TACHE = [
  'demarrer',
  'attendre_tiers',
  'reprendre',
  'terminer',
  'rouvrir',
  'annuler',
] as const
export type EvenementTacheSaisi = (typeof EVENEMENTS_TACHE)[number]

const unSeulExecutant = (t: {
  executantPersonneId?: string | null
  executantTiersId?: string | null
}) => !(t.executantPersonneId && t.executantTiersId)
const messageExecutant = {
  message: 'Un seul exécutant : une personne ou un tiers.',
  path: ['executantTiersId'],
}

/** Tâche saisie à la main ; rattachée à une échéance ou un paiement, elle en prend l'entité. */
export const tacheCreation = z
  .object({
    titre: texte.max(300),
    description: texte.max(5000).nullable().optional(),
    entiteId: z.uuid().nullable().optional(),
    echeanceId: z.uuid().optional(),
    paiementId: z.uuid().optional(),
    executantPersonneId: z.uuid().nullable().optional(),
    executantTiersId: z.uuid().nullable().optional(),
    garantId: z.uuid().nullable().optional(),
    dateDue: z.iso.date().nullable().optional(),
    urgente: z.boolean().optional(),
    /** Créée dans ce projet (elle en prend l'entité, sauf entité dite). */
    projetId: z.uuid().optional(),
  })
  .refine((t) => !(t.echeanceId && t.paiementId), {
    message: 'Une échéance ou un paiement, pas les deux.',
    path: ['paiementId'],
  })
  .refine(unSeulExecutant, messageExecutant)

/**
 * Une nouvelle date due devient une date forcée ; « dateForcee: false » la rend au générateur.
 * L'entité se change pour une tâche libre (ni échéance, ni paiement, ni pièce) : un mail
 * classé sur la mauvaise entité, par exemple.
 */
export const tacheModification = z
  .object({
    entiteId: z.uuid().nullable(),
    titre: texte.max(300),
    description: texte.max(5000).nullable(),
    dateDue: z.iso.date().nullable(),
    dateForcee: z.literal(false),
    urgente: z.boolean(),
  })
  .partial()

/** Affecter : exécutant (personne ou tiers, null pour « à prendre ») et garant (null : responsables). */
export const affectationTache = z
  .object({
    executantPersonneId: z.uuid().nullable().optional(),
    executantTiersId: z.uuid().nullable().optional(),
    garantId: z.uuid().nullable().optional(),
  })
  .refine(unSeulExecutant, messageExecutant)

export const changementStatutTache = z.object({ evenement: z.enum(EVENEMENTS_TACHE) })

/**
 * Reporter une tâche ouverte : à un jour (« date »), au bilan de son entité (« bilan »), ou la
 * réveiller (« reveiller »). Le motif, facultatif, devient un commentaire.
 */
export const reportTache = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('date'), au: z.iso.date(), motif: texte.max(5000).optional() }),
  z.object({ mode: z.literal('bilan'), motif: texte.max(5000).optional() }),
  z.object({ mode: z.literal('reveiller') }),
])
export type ReportTache = z.infer<typeof reportTache>

export const commentaireCreation = z.object({ texte: texte.max(5000) })

export type TacheCreation = z.infer<typeof tacheCreation>

/** Une tâche dictée : le texte transcrit, relu par l'humain, que Claude met en champs. */
export const dicteeTache = z.object({ texte: texte.max(5000) })

const TITRE_DICTE_MAX = 120

/**
 * Repli sans modèle (clé absente, plafond atteint, appel en échec) : la première phrase fait
 * le titre, coupée à un mot ; le texte entier fait les précisions, sauf s'il n'est que le titre.
 */
export function tacheDepuisDictee(dicte: string): { titre: string; description: string | null } {
  const entier = dicte.replace(/\s+/g, ' ').trim()
  const phrase = /^(.+?)[.!\n](\s|$)/.exec(entier)?.[1]?.trim() ?? entier
  let titre = phrase
  if (titre.length > TITRE_DICTE_MAX) {
    const coupe = titre.slice(0, TITRE_DICTE_MAX - 1)
    const espace = coupe.lastIndexOf(' ')
    titre = `${(espace > 40 ? coupe.slice(0, espace) : coupe).trimEnd()}…`
  }
  return { titre, description: entier === titre ? null : dicte.trim() }
}
export type TacheModification = z.infer<typeof tacheModification>
export type AffectationTache = z.infer<typeof affectationTache>
